import { PartialLoadingFormData } from "../../app/models/api/orderTransportation/partialLoadingFormData";
import { FullLoadingFormData } from "../../app/models/api/orderTransportation/fullLoadingFormData";
import { LoadingTypeEnum } from "../../app/enums/loadingTypeEnum";
import { DeliveryInputResult } from "../../app/models/features/deliveryOptions/deliveryInputResult";
import { SelectedDeliveryOption } from "../../app/models/features/deliveryOptions/selectedDeliveryOption";
import { AppDispatch } from "../../app/store";
import { setPartialLoadingData, setFullLoadingData, setDeliveryInputFrom, setDeliveryInputTo } from "../../pages/orderPipeline/orderForm/orderFormSlice";
import { setDeliveryOption } from "../../pages/orderPipeline/deliveryOptions/deliveryOptionSlice";
import { setNextBarStep } from "../../features/mainProgressBar/mainProgressBarSlice";
import { MainProgressBarEnum } from "../../app/enums/mainProgressBarEnum";

const ORDER_BACKUP_KEY = "vega_order_backup";

// 24 hours in milliseconds
const EXPIRY_TIME = 24 * 60 * 60 * 1000;

export interface OrderBackup {
    loadingType: LoadingTypeEnum;
    partialLoadingFormData: PartialLoadingFormData;
    fullLoadingFormData?: FullLoadingFormData;
    deliveryFrom: DeliveryInputResult;
    deliveryTo: DeliveryInputResult;
    deliveryOption: SelectedDeliveryOption;
    timestamp: number;
}

export const saveOrderToLocalStorage = (
    data: Omit<OrderBackup, "timestamp">
): void => {
    const backup: OrderBackup = {
        ...data,
        timestamp: Date.now(),
    };
    localStorage.setItem(ORDER_BACKUP_KEY, JSON.stringify(backup));
};

export const getOrderFromLocalStorage = (): OrderBackup | null => {
    try {
        const data = localStorage.getItem(ORDER_BACKUP_KEY);
        if (!data) return null;

        const backup: OrderBackup = JSON.parse(data);

        // Check if data has expired (24 hours)
        if (Date.now() - backup.timestamp > EXPIRY_TIME) {
            clearOrderFromLocalStorage();
            return null;
        }

        return backup;
    } catch {
        clearOrderFromLocalStorage();
        return null;
    }
};

export const clearOrderFromLocalStorage = (): void => {
    localStorage.removeItem(ORDER_BACKUP_KEY);
};

/**
 * Restores order data from localStorage to Redux state.
 * Returns true if order was restored, false otherwise.
 */
export const restoreOrderFromLocalStorage = (dispatch: AppDispatch): boolean => {
    const orderBackup = getOrderFromLocalStorage();
    if (!orderBackup) return false;

    dispatch(setPartialLoadingData(orderBackup.partialLoadingFormData));
    if (orderBackup.loadingType === LoadingTypeEnum.Full && orderBackup.fullLoadingFormData) {
        dispatch(setFullLoadingData(orderBackup.fullLoadingFormData));
    }
    dispatch(setDeliveryInputFrom(orderBackup.deliveryFrom));
    dispatch(setDeliveryInputTo(orderBackup.deliveryTo));
    dispatch(setDeliveryOption(orderBackup.deliveryOption));
    dispatch(setNextBarStep({ nextStep: MainProgressBarEnum.RecommendedServices }));
    clearOrderFromLocalStorage();

    return true;
};
